<template>
  <div class="row">
    <div v-if="loading" class="col-12 col-sm-4" >
      <span class="loading text-muted">Loading...</span>
    </div>
    <div v-if="!loading" class="col-12 col-sm-4" >
      <div class="row">
        <div class="col-12 col-sm-6" >
          <p><small class="text-muted">Last Month</small><br><span style="font-size: 22px">{{ lastMonthTrend }}</span></p>
        </div>
        <div class="col-12 col-sm-6" >
          <p><small class="text-muted">Last Year</small><br><span style="font-size: 22px">{{ lastYearTrend }}</span></p>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-sm-6" >
          <p><small class="text-muted">Monthly Trend</small><br><span style="font-size: 22px">{{ monthlyTrend }}</span></p>
        </div>
        <div class="col-12 col-sm-6" >
          <small class="text-muted">Asset Utilization</small>
          <div class="row pl-3 pr-3 mt-2">
            <div class="col-12 col-sm-7" style="height: 10px; background-color: #e5e5e5; border-radius: 10px 0 0 10px;"></div>
            <div class="col-12 col-sm-5" style="height: 10px; border-radius: 0 10px 10px 0;
              background-image: linear-gradient(90deg, #ffffff 25%, #e5e5e5 25%, #e5e5e5 50%, #ffffff 50%, #ffffff 75%, #e5e5e5 75%, #e5e5e5 100%);
              background-size: 12.00px 12.00px;"></div>
          </div>
          <div class="row">
            <div class="col-6"><small>HIGH</small></div>
            <div class="col-6 text-right"><small>LOW</small></div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!loading" class="col-12 col-sm-8" >
      <line-chart
        style="height:200px;"
        :chartdata="chartdata"
        :options="options"
        />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'

import LineChart from '../../components/chart/LineChart.vue'

import apiService from '../../services/api.service'

import { checkResponseErrors } from '../../utils/http'
import moment from 'moment'

export default {
  name: 'CarbonTonneKmWidget',
  data: () => ({
    loading: true,
    chartdata: null,
    options: null,
    monthlyTrend: "TBD",
    lastMonthTrend: "TBD",
    lastYearTrend: "TBD",
  }),
  components: {
    LineChart
  },
  async mounted () {
    this.loading = true;

    try {
      const resp = await apiService.get('/dashboard/carbonTonneKM');
      if(await checkResponseErrors(resp)) return;

      // Side Data
      this.lastMonthTrend = formatNumber(resp.data.trend.lastMonth)
      this.lastYearTrend = formatNumber(resp.data.trend.lastYear)
      this.monthlyTrend = formatNumber(resp.data.trend.monthly)

      // Chart
      var endDate = moment().endOf('day');
      var startDate = moment().startOf('day').subtract(90, 'days');
      this.chartdata = initWidgetData(resp.data, startDate, endDate);
      this.options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 20
          }
        },
        scales: {
          xAxes: [{
            type: 'time',
            gridLines: {
              drawOnChartArea: false,
              drawTicks: true,
              color: 'white',
              zeroLineColor: 'white'
            },
            stacked: true,
            time: {
              unit: 'month',
              unitStepSize: 0.5,
              displayFormats: {
                month: 'MMM DD'
              },
              tooltipFormat: 'MM/DD/YYYY',
            },
            ticks: {
              min: startDate,
              max: endDate,
            }
          }],
          yAxes: [{
            id: 'carbonAxis',
            display: false,
            stacked: true,
            gridLines: {
              drawBorder: false,
              drawOnChartArea: false,
              drawTicks: false
            },
            ticks: {
              fontColor: '#cdcfd1',
              padding: 5,
              maxTicksLimit: 5,
              beginsAtZero: true,
              callback: (value, index, values) => {
                return formatNumber(value)
              },
            },
            afterDataLimits(scale) {
              var trendMin = scale.chart.scales.trendAxis.options.ticks.suggestedMin
              var trendMax = scale.chart.scales.trendAxis.options.ticks.suggestedMax
              var carbonMin = scale.min
              var carbonMax = scale.max
              if(trendMin != carbonMin || trendMax != carbonMax){
                scale.chart.scales.trendAxis.options.ticks.suggestedMin = scale.min;
                scale.chart.scales.trendAxis.options.ticks.suggestedMax = scale.max;
                scale.chart.update();
              }
            }
          },
          {
            id: 'trendAxis',
            ticks: {
              fontColor: '#cdcfd1',
              padding: 5,
              maxTicksLimit: 5,
              beginsAtZero: true,
              callback: (value, index, values) => {
                return formatNumber(value)
              },
            },
            gridLines: {
              drawBorder: false,
              drawOnChartArea: false,
              drawTicks: false
            }
          },
          ]
        },
        tooltips: {
          mode: 'nearest',
          intersect: true,
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';

              if (label) {
                  label += ': ';
              }
              label += formatNumber(tooltipItem.yLabel);
              label += "g Co2 / Tonne KM"
              return label;
            }
          }
        },
        plugins: {
          filler: {
            propagate: true
          }
        }
      }

      this.loading = false;
    } catch (error) {
      Vue.toasted.error(error, { duration: 3500 });
      return;
    }
  }, // end mounted
}

const initWidgetData = (data, startDate, endDate) => {
  var widgData = {};

  widgData.datasets = []
  // Build Trend Dataset
  if(data.trend.data.some(trend => trend.trend != 0)){
    var trendDataset = {};
    trendDataset.label = "Trend"
    trendDataset.borderColor = "#6bd38f"
    trendDataset.backgroundColor = "#6bd38f"
    trendDataset.fill = false
    trendDataset.borderDash = [3, 3]
    trendDataset.borderWidth = 3
    trendDataset.pointRadius = 0
    trendDataset.pointHitRadius = 3
    trendDataset.lineTension = 0
    trendDataset.borderJoinStyle = 'bevel'
    trendDataset.yAxisID = 'trendAxis'
    trendDataset.data = []

    data.trend.data.forEach((trendElement) => {
      // Build Trend Points
      var trendPoint = {};
      trendPoint.x = moment(trendElement.date)
      trendPoint.y = trendElement.trend
      trendDataset.data.push(trendPoint)
    })
    widgData.datasets.push(trendDataset)
  }

  data.data.forEach((element, i) => {
    // Build Chart Dataset
    var widgDataset = {};
    widgDataset.label = getDatasetLabel(element._id)
    widgDataset.backgroundColor = getDatasetBackgroundColor(widgDataset.label)
    widgDataset.borderColor = getDatasetBackgroundColor(widgDataset.label)
    widgDataset.pointRadius = 0
    widgDataset.pointHitRadius = 3
    widgDataset.lineTension = 0
    widgDataset.spanGaps = false
    widgDataset.borderJoinStyle = 'bevel'
    widgDataset.yAxisID = 'carbonAxis'
    widgDataset.data = []

    // Build Dataset Points
    var date = moment(startDate)
    var lastPoint = null
    var j = 0
    while(date.isSameOrBefore(endDate, 'days')){
      var y = NaN
      if(widgDataset.data[j - 1] != null && widgDataset.data[j - 1].y > 0){
        y = 0 // sets to zero so chart draws a line that goes from number to zero
      }
      element.points.forEach((elementPoint) => {
        if(moment(elementPoint.date).isSame(date, 'days')){
          y = elementPoint.CO2_TONNEKM

          if(widgDataset.data[j - 1] != null && isNaN(widgDataset.data[j - 1].y)){
            widgDataset.data[j - 1].y = 0 // sets previows point to zero so chart draws a line that goes from zero to number
          }
        }
      });

      var widgPoint = {};
      widgPoint.x = moment(date)
      widgPoint.y = y
      widgDataset.data.push(widgPoint)

      date = date.add(1, 'days')
      lastPoint = widgPoint
      j++
    }
    widgData.datasets.push(widgDataset)
  });
  
  widgData.datasets.sort((datasetA, datasetB) => getDatasetOrder(datasetB.label) - getDatasetOrder(datasetA.label))

  return widgData
}

const getDatasetLabel = (id) => {
  switch(id){
    case "Flatbed":
      return "Flatbed"
    case "Container Chassis":
      return "Container"
    case "Reefer Fresh":
      return "Reefer-Fresh"
    case "Reefer Frozen":
      return "Reefer-Frozen"
    case "Heated":
      return "Heated"
    case "Dry Van":
      return "Dry"
    default:
      return id
  }
}

const getDatasetBackgroundColor = (label) => {
  switch(label){
    case "Flatbed":
      return "#bb5dff"
    case "Container":
      return "#473dfb"
    case "Reefer-Fresh":
      return "#57bc45"
    case "Reefer-Frozen":
      return "#07b5fb"
    case "Heated":
      return "#fb145a"
    case "Dry":
    default:
      return "#e5e5e5"
  }
}

const getDatasetOrder = (label) => {
  switch(label){
    case "Dry":
      return 1
    case "Flatbed":
      return 2
    case "Container":
      return 3
    case "Reefer-Fresh":
      return 4
    case "Reefer-Frozen":
      return 5
    case "Heated":
      return 6
    default:
      return 10
    case "Trend":
      return 30
  }
}

const formatNumber = (value) => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

  return formatter.format(value)
}
</script>



// WEBPACK FOOTER //
// CarbonTonneKMWidget.vue?c784ec4e